import { RefObject, useLayoutEffect, useState } from 'react';

// Window sizes at which the roadmap swaps the Play bar for the level-preview sheet.
//
// Height: the Play bar is about 200px tall and fixed to the bottom of the window.
// Below 600px of height it covers the lowest row of level nodes and their names
// (measured in Chromium at 768 to 1440 wide: the bar reaches a node name from
// 590px down and a node itself from 550px down), so the sheet takes over there.
// Width: a window 480px wide or less has no room for the bar's three columns.
export const ROADMAP_SHEET_MAX_WIDTH = 480;
export const ROADMAP_SHEET_MAX_HEIGHT = 599;

export const roadmapSheetQuery = `(max-width:${ROADMAP_SHEET_MAX_WIDTH}px), (max-height:${ROADMAP_SHEET_MAX_HEIGHT}px)`;

export const isRoadmapSheetViewport = (width: number, height: number): boolean =>
  width <= ROADMAP_SHEET_MAX_WIDTH || height <= ROADMAP_SHEET_MAX_HEIGHT;

// The path artwork and the level nodes on it are sized from the window HEIGHT
// only. A narrower window keeps the same path and scrolls further sideways; the
// width must never change the artwork's height without moving the nodes with it.
export type RoadmapPathScale = 'small' | 'medium-small' | 'medium' | 'large';

const pathScaleHeightLimits: Array<[RoadmapPathScale, number]> = [
  ['small', 700],
  ['medium-small', 840],
  ['medium', 905],
];

export const roadmapPathScale = (viewportHeight: number): RoadmapPathScale =>
  pathScaleHeightLimits.find(([, limit]) => viewportHeight <= limit)?.[0] ?? 'large';

const pathImageHeights: Record<RoadmapPathScale, number> = {
  small: 200,
  'medium-small': 320,
  medium: 400,
  large: 480,
};

export const roadmapPathImageHeight = (scale: RoadmapPathScale): number => pathImageHeights[scale];

// The Play bar's columns: the text column (title, description, tier segments; 30px of
// padding each side) and the preview box share what the fixed-width Play column leaves,
// half each, the text column's padding on top of its half.
export const ROADMAP_PLAY_COLUMN_WIDTH = 160;
export const ROADMAP_TEXT_COLUMN_PADDING = 30;
// The phrase sits in the preview box with 5px on each side.
const PREVIEW_PHRASE_SIDE_INSETS = 10;
// Below this height a picture of the phrase is too small to read (the sheet's minimum).
export const ROADMAP_PREVIEW_MIN_PHRASE_HEIGHT = 72;

/** Width of the Play bar's preview box for a bar of the given width. */
export const roadmapPreviewColumnWidth = (barWidth: number): number =>
  (barWidth - ROADMAP_PLAY_COLUMN_WIDTH - 2 * ROADMAP_TEXT_COLUMN_PADDING) / 2;

/** Width the phrase itself gets inside the preview box. */
export const roadmapPreviewPhraseRoom = (barWidth: number): number =>
  roadmapPreviewColumnWidth(barWidth) - PREVIEW_PHRASE_SIDE_INSETS;

export interface PreviewPhraseSize {
  width: number;
  height: number;
}

/**
 * For a picture of the phrase: the narrowest it can be drawn and still be 72px high.
 * (It is never drawn larger than its own pixels, so one shorter than 72px never is.)
 */
export const roadmapPictureMinPhraseWidth = (picture: PreviewPhraseSize): number =>
  picture.height < ROADMAP_PREVIEW_MIN_PHRASE_HEIGHT
    ? Infinity
    : (ROADMAP_PREVIEW_MIN_PHRASE_HEIGHT * picture.width) / picture.height;

// Used until the renderer knows its phrase's minimum width: the widest picture measured
// (level 9, 654x142).
const FALLBACK_MIN_PHRASE_WIDTH = roadmapPictureMinPhraseWidth({ width: 654, height: 142 });

// A bar width of 0 means it has not been measured yet: keep the preview until it is.
export const roadmapBarShowsPreview = (barWidth: number, minPhraseWidth: number | null): boolean =>
  barWidth <= 0 ||
  roadmapPreviewPhraseRoom(barWidth) >= (minPhraseWidth ?? FALLBACK_MIN_PHRASE_WIDTH);

/**
 * Whether the Play bar shows its preview box. Decided from the bar's width (watched with
 * a ResizeObserver, not a window media query, so it follows the sidebar too), which does
 * not change when the box hides, so there is no feedback loop. `minPhraseWidth` is the
 * narrowest width at which the phrase renderer stays legible, or null until it knows
 * (a fallback is used); a renderer must not measure itself while the box is hidden.
 */
export const useRoadmapPreviewFits = (
  barRef: RefObject<HTMLElement | null>,
  minPhraseWidth: number | null,
): boolean => {
  const [barWidth, setBarWidth] = useState(0);

  useLayoutEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    const readBarWidth = () => setBarWidth(bar.getBoundingClientRect().width);
    readBarWidth();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', readBarWidth);
      return () => window.removeEventListener('resize', readBarWidth);
    }
    const observer = new ResizeObserver(readBarWidth);
    observer.observe(bar);
    return () => observer.disconnect();
  }, [barRef]);

  return roadmapBarShowsPreview(barWidth, minPhraseWidth);
};

export interface MapTranslateYLimits {
  // The map's offset now, and the centres (screen y) of its highest and lowest level
  // nodes at that offset.
  translateYNow: number;
  topNodeCentre: number;
  bottomNodeCentre: number;
  // The part of the path area that is visible: below its top edge, above the open sheet.
  visibleBand: { top: number; bottom: number };
  // How far inside the band a node is kept.
  edgePadding: number;
}

// The map moves up or down (as a whole) to bring the selected level into the visible
// part of the path area. A path taller than that part moves until its highest or lowest
// node reaches the edge, and no further; a path that fits moves only as far as keeps every
// node inside it. Either way a move that brings the selected node into view still does.
export const clampMapTranslateY = (candidate: number, limits: MapTranslateYLimits): number => {
  const { translateYNow, topNodeCentre, bottomNodeCentre, visibleBand, edgePadding } = limits;
  const topNodeAtTopEdge = translateYNow + visibleBand.top + edgePadding - topNodeCentre;
  const bottomNodeAtBottomEdge =
    translateYNow + visibleBand.bottom - edgePadding - bottomNodeCentre;
  return Math.min(
    Math.max(candidate, Math.min(topNodeAtTopEdge, bottomNodeAtBottomEdge)),
    Math.max(topNodeAtTopEdge, bottomNodeAtBottomEdge),
  );
};
